import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { TrendingUp, Handshake, Trophy, Crown, Gift, Zap, User, AlertTriangle, Package, Wallet, Banknote, Users, Globe, Calendar, Briefcase, Landmark, Lock } from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/users/dashboard').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : 'N/A';

  const wallets = [
    { label: 'ROI Wallet', key: 'roiWallet', icon: TrendingUp, color: 'var(--primary)' },
    { label: 'Direct Wallet', key: 'directWallet', icon: Handshake, color: '#22c55e' },
    { label: 'Level Wallet', key: 'levelWallet', icon: Trophy, color: '#3b82f6' },
    { label: 'Royalty Wallet', key: 'royaltyWallet', icon: Crown, color: '#a855f7' },
    { label: 'Reward Wallet', key: 'rewardWallet', icon: Gift, color: '#f59e0b' },
  ];

  if (loading) return <DashboardLayout><div className="loader-wrap"><div className="loader"></div></div></DashboardLayout>;

  const u = data?.user || user;

  return (
    <DashboardLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Welcome back, <strong style={{ color: 'var(--primary)' }}>{u?.name}</strong></p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {!u?.isActive && (
            <Link to="/dashboard/package" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Zap size={16} /> Activate Package
            </Link>
          )}
          <Link to="/dashboard/profile" className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <User size={14} /> Profile
          </Link>
        </div>
      </div>

      {/* Status banner */}
      {!u?.isActive && (
        <div className="alert alert-warning" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <AlertTriangle size={16} style={{ flexShrink: 0 }} /> Your package is not active. <Link to="/dashboard/package" style={{ color: 'var(--primary)', fontWeight: 700 }}>Activate now</Link> to start earning ROI income.
        </div>
      )}

      {/* Referral box — only shown once the user has an active package.
          Referring people before activating a package would let someone
          build a downline without ever becoming a paying member themselves,
          so the link stays locked until then. */}
      {u?.isActive ? (
        <div className="referral-box" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>Your Referral Code</div>
              <div className="referral-code-display">{u?.referralCode}</div>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 6 }}>Referral Link</div>
              <div className="copy-link">
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {window.location.origin}/register/{u?.referralCode}
                </span>
                <button className="copy-btn" onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/register/${u?.referralCode}`);
                  alert('Copied!');
                }}>Copy</button>
              </div>
            </div>
          </div>

          {/* Share buttons */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
            {(() => {
              const referralUrl = `${window.location.origin}/register/${u?.referralCode}`;
              const shareText = `Join Triveni Pay using my referral code ${u?.referralCode || ''} and start earning! ${referralUrl}`;
              return (
                <>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn share-btn-whatsapp"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12.001 2.003c-5.514 0-9.997 4.483-9.997 9.997 0 1.763.464 3.48 1.345 4.988l-1.43 5.22 5.343-1.402a9.96 9.96 0 0 0 4.74 1.19h.005c5.513 0 9.996-4.483 9.996-9.996 0-2.67-1.04-5.18-2.929-7.069a9.933 9.933 0 0 0-7.073-2.928zm0 18.184h-.004a8.19 8.19 0 0 1-4.174-1.145l-.3-.178-3.17.831.846-3.09-.195-.317a8.187 8.187 0 0 1-1.256-4.366c0-4.516 3.674-8.19 8.19-8.19a8.14 8.14 0 0 1 5.792 2.4 8.14 8.14 0 0 1 2.397 5.795c0 4.516-3.673 8.19-8.126 8.26z"/></svg>
                    WhatsApp
                  </a>
                  <a
                    href={`https://t.me/share/url?url=${encodeURIComponent(referralUrl)}&text=${encodeURIComponent(`Join Triveni Pay using my referral code ${u?.referralCode || ''}!`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn share-btn-telegram"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M21.198 2.433a2.242 2.242 0 0 0-1.022.215l-16.5 6.9c-1.045.437-1.035 1.045-.19 1.31l4.24 1.324 1.63 5.007c.198.526.339.72.68.72.245 0 .361-.113.548-.32l1.606-1.616 4.235 3.13c.78.43 1.343.207 1.54-.723l2.784-13.12c.291-1.143-.418-1.665-1.15-1.827zm-11.51 11.31l-1.735-.55L17.5 6.837c.24-.15.463.05.263.226z"/></svg>
                    Telegram
                  </a>
                </>
              );
            })()}
          </div>
        </div>
      ) : (
        <div className="referral-box referral-box-locked" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div className="referral-lock-icon"><Lock size={20} /></div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 700, color: 'var(--text-white)', marginBottom: 4 }}>Your Referral Link is Locked</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>Activate a package to unlock your referral code and start inviting your team.</div>
            </div>
            <Link to="/dashboard/package" className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Zap size={14} /> Activate Package
            </Link>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        {[
          { icon: Package, label: 'Active Package', value: u?.activePackage ? `₹${u.activePackage.toLocaleString('en-IN')}` : 'No Package', color: 'var(--primary)' },
          { icon: Wallet, label: 'Total Earned', value: formatCurrency(u?.totalEarned), color: '#22c55e' },
          { icon: Banknote, label: 'Total Withdrawn', value: formatCurrency(u?.totalWithdrawn), color: '#3b82f6' },
          { icon: Users, label: 'Direct Members', value: u?.totalDirects || 0, color: '#a855f7' },
          { icon: Globe, label: 'Total Team', value: u?.totalTeam || 0, color: '#f59e0b' },
          { icon: Calendar, label: 'Pkg Expires', value: formatDate(u?.packageExpiresAt), color: 'var(--text-muted)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-icon" style={{ background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}><s.icon size={22} /></div>
            <div className="stat-info">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color, fontSize: '1.1rem' }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Wallet balances */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Briefcase size={18} /> Wallet Balances</h3>
          <Link to="/dashboard/withdraw" className="btn btn-primary btn-sm">Withdraw</Link>
        </div>
        <div className="wallet-grid">
          {wallets.map(w => (
            <div key={w.key} className="wallet-card">
              <div style={{ marginBottom: 6, color: w.color, display: 'flex', justifyContent: 'center' }}><w.icon size={24} /></div>
              <div className="wallet-label">{w.label}</div>
              <div className="wallet-value" style={{ color: w.color }}>
                {formatCurrency(u?.[w.key])}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid-2">
        {/* Recent Income */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Wallet size={18} /> Recent Income</h3>
            <Link to="/dashboard/income" className="btn btn-outline btn-sm">View All</Link>
          </div>
          {data?.recentIncomes?.length > 0 ? (
            <div>
              {data.recentIncomes.map(inc => {
                const IncIcon = inc.type === 'roi' ? TrendingUp : inc.type === 'direct' ? Handshake : inc.type === 'level' ? Trophy : Gift;
                return (
                <div key={inc._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-white)', textTransform: 'capitalize', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <IncIcon size={14} /> {inc.type} Income
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {inc.fromUser ? `From: ${inc.fromUser.name}` : inc.description || 'Daily ROI'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#22c55e', fontWeight: 700 }}>+{formatCurrency(inc.amount)}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{new Date(inc.createdAt).toLocaleDateString('en-IN')}</div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon"><Banknote size={40} /></div>
              <h3>No income yet</h3>
              <p style={{ fontSize: '0.85rem' }}>Activate your package to start earning</p>
            </div>
          )}
        </div>

        {/* Recent Withdrawals */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Banknote size={18} /> Recent Withdrawals</h3>
            <Link to="/dashboard/withdraw" className="btn btn-outline btn-sm">View All</Link>
          </div>
          {data?.recentWithdrawals?.length > 0 ? (
            <div>
              {data.recentWithdrawals.map(w => (
                <div key={w._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-white)' }}>Withdrawal Request</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{new Date(w.createdAt).toLocaleDateString('en-IN')}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'var(--primary)', fontWeight: 700 }}>{formatCurrency(w.amount)}</div>
                    <span className={`badge badge-${w.status === 'approved' ? 'success' : w.status === 'rejected' ? 'danger' : 'warning'}`}>
                      {w.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon"><Landmark size={40} /></div>
              <h3>No withdrawals yet</h3>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}